import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useDisconnectSquare, useSquareConnection } from "../hooks/squareQueries";
import { useSquareConnect } from "../hooks/useSquareConnect";

function SquareSettings({ businessId, locationId, userId }: { businessId: string; locationId: string; userId: string }) {
  const query = useSquareConnection(businessId);
  const disconnect = useDisconnectSquare(businessId);
  const flow = useSquareConnect(businessId, locationId, userId, () => { void query.refetch(); });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const connection = query.data;
  return (
    <Page>
      <PageHeader title="Square" />
      <PageContent>
        <Section title="Square connection" card>
          <p className="text-sm text-muted-foreground">Connect your US Square account using Square's hosted authorization. This connection is shared across your business.</p>
          <p className="text-sm text-muted-foreground">Payments are not available in Froppio. Connecting does not verify your identity or enable payments.</p>
          {query.isPending ? <LoadingSpinner message="Loading Square connection..." /> : query.error ? (
            <ErrorState title="Couldn't load Square connection" message="Connection status is unavailable. Please try again." onRetry={() => void query.refetch()} />
          ) : connection && <>
            <p className="font-medium">{connection.status === "connected" ? "Connected" : connection.status === "reconnect_required" ? "Reconnect required" : "Not connected"}</p>
            <p className="text-sm text-muted-foreground">Environment: {connection.environment === "sandbox" ? "Sandbox" : "Production"}</p>
            {connection.merchant && <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt>Merchant</dt><dd>{connection.merchant.businessName ?? "Square merchant"}</dd>
              <dt>Merchant ID</dt><dd className="break-all">{connection.merchant.id}</dd>
              <dt>Country</dt><dd>{connection.merchant.country}</dd>
              <dt>Currency</dt><dd>{connection.merchant.currency}</dd>
            </dl>}
            <div className="flex flex-wrap gap-2">
              {connection.status !== "connected" && <Button disabled={flow.busy || disconnect.isPending} onClick={() => void flow.connect()}>
                {flow.busy ? "Waiting for Square…" : connection.status === "reconnect_required" ? "Reconnect Square" : "Connect Square"}
              </Button>}
              {connection.status !== "disconnected" && <Button variant="outline" disabled={flow.busy || disconnect.isPending} onClick={() => { disconnect.reset(); setConfirmOpen(true); }}>Disconnect Square</Button>}
            </div>
          </>}
          <Button variant="outline" disabled={query.isFetching} onClick={() => void query.refetch()}>Refresh status</Button>
          {flow.message && <p role="status" className="text-sm">{flow.message}</p>}
          {flow.fallback && <div className="space-y-3">
            <p className="text-sm">The Square window could not open. Continue here to authorize with Square.</p>
            <Button onClick={flow.continueInTab}>Continue in this tab</Button>
          </div>}
        </Section>
        <ConfirmationDialog open={confirmOpen} onOpenChange={setConfirmOpen}
          title="Disconnect Square?" description="This removes the Square connection for your entire business. You can connect again later."
          confirmLabel="Disconnect" dismissLabel="Keep connected" destructive pending={disconnect.isPending}
          error={disconnect.error ? "Couldn't disconnect Square. Please try again." : undefined}
          onConfirm={() => disconnect.mutate(undefined, { onSuccess: () => setConfirmOpen(false) })} />
      </PageContent>
    </Page>
  );
}

export function SquarePage() {
  const { user } = useAuth();
  const { business, businessId, loading } = useCurrentBusiness();
  const { locationId } = useParams();
  if (!loading && business && businessId && locationId && user && business.ownerId === user.id) {
    return <SquareSettings key={`${businessId}:${locationId}:${user.id}`} businessId={businessId} locationId={locationId} userId={user.id} />;
  }
  return <Page><PageHeader title="Square" /><PageContent>
    {loading ? <LoadingSpinner message="Loading business..." /> :
      <ErrorState message={business ? "Only the business owner can manage Square." : "Select a business to manage Square."} />}
  </PageContent></Page>;
}
